import { Stack, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { FC, useMemo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import './AIAssistantSettings.scss';
import { aiAssistantSettingsSections } from '../../constants';

interface SettingsItem {
  title: string;
  titleKey: string;
  component: React.ComponentType;
  disabled?: boolean;
}

interface SettingsSection {
  title: string;
  titleKey: string;
  items: SettingsItem[];
}

const AIAssistantSettings: FC = () => {
  const { t } = useTranslation();

  const renderAccordion = (item: SettingsItem): ReactNode => {
    const accordionClassName = item.disabled ? 'ai-assistant-settings__accordion--disabled' : '';

    return (
      <Accordion
        key={item.titleKey}
        disableGutters
        elevation={0}
        className={accordionClassName}
        disabled={item.disabled}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          className="ai-assistant-settings__accordion-summary"
          sx={{ padding: 0 }}
        >
          <Typography className="ai-assistant-settings__item-title" variant="body2">
            {t(item.titleKey)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }} className="ai-assistant-settings__accordion-details">
          <item.component />
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderSection = (section: SettingsSection): ReactNode => (
    <Stack key={section.titleKey} gap={1}>
      <Typography variant="subtitle1" className="ai-assistant-settings__subtitle">
        {t(section.titleKey)}
      </Typography>
      <Stack>{section.items.map(renderAccordion)}</Stack>
    </Stack>
  );

  const renderHeader = (): ReactNode => (
    <Typography mb={1} variant="body1" className="ai-assistant-settings__title">
      {t('settings.nav.aiAssistant')}
    </Typography>
  );

  const renderContent = (): ReactNode => (
    <Stack gap={2}>{aiAssistantSettingsSections.map(renderSection)}</Stack>
  );

  return (
    <Stack className="ai-assistant-settings">
      {renderHeader()}
      {renderContent()}
    </Stack>
  );
};

export default AIAssistantSettings;
